import React, { useEffect, useState } from "react";

const TOUR_STEPS = [
  { title: "AI Live Coach", description: "AI Live Coach provides actionable guidance while keeping the final trading decision under the user's control." },
  { title: "Quick Trade", description: "Quick Trade organizes order details, risk controls, review steps, and confirmation information in one focused panel." },
  { title: "AI Decision", description: "AI Decision summarizes the current setup, confidence, risk, and missing confirmations so the user can make an informed final choice." },
  { title: "Professional Chart", description: "The Professional Chart brings price action, volume, indicators, support, resistance, and trade context together in the main analysis workspace." },
  { title: "AI SmartFlow", description: "AI SmartFlow combines liquidity, order flow, institutional activity, volume, VWAP, momentum, and related risk signals into one decision-support view." },
  { title: "Market Structure Analysis", description: "Market Structure Analysis explains trend direction, higher highs and lows, support and resistance, breakouts, pullbacks, and invalidation levels." },
  { title: "Account Summary", description: "Account Summary presents authenticated account information, buying power, portfolio equity, exposure, and profit or loss when the connected broker provides it." },
  { title: "Trade Management", description: "Trade Management helps users monitor positions, protective stops, profit targets, partial exits, and order status while keeping control of the trade." },
  { title: "Scanner", description: "Scanner helps users discover and rank trading opportunities while preserving filters, selected scanner views, and prior results during review." },
  { title: "Portfolio", description: "Portfolio is designed as the broker-backed source of truth for positions, balances, transaction history, performance, and account-wide risk visibility." },
  { title: "Academy", description: "Academy teaches trading concepts through guided lessons, visual examples, knowledge checks, and AI explanations that connect indicators with market context." },
  { title: "Journal", description: "Journal records trade plans, decisions, outcomes, lessons, and review notes so users and the AI Assistant can identify patterns and improve discipline over time." },
  { title: "Tax Forms", description: "Tax Forms provides a dedicated place for investment tax documents, capital gains information, and educational tax tools without changing account or trade data." },
  { title: "Settings", description: "Settings lets users adjust workspace preferences and interface density while keeping professional chart sizing and trading safeguards independent." },
];

const TOUR_ID_BY_TITLE = {
  "AI Live Coach": "nina-live-coach",
  "Quick Trade": "quick-trade",
  "AI Decision": "nina-decision-command",
   "Professional Chart": "professional-chart",
   "Market Structure Analysis": "nina-smartflow",
   "Account Summary": "account-summary",
  Scanner: "scanner",
  Portfolio: "portfolio",
  Academy: "academy",
  Journal: "journal",
  "Tax Forms": "tax-forms",
  Settings: "settings",
};

const TOUR_SELECTOR_BY_TITLE = {
  "Trade Management": ".partialProfitSection.partialProfitStandalone",
};

const FALLBACK_RECT = { top: 64, left: 240, width: 760, height: 320 };
const overlayStyle = { position: "fixed", inset: 0, zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px", boxSizing: "border-box", background: "rgba(2, 6, 23, 0.18)" };
const spotlightLabelStyle = { position: "absolute", top: "12px", left: "12px", margin: 0, padding: "7px 10px", borderRadius: "999px", background: "rgba(8, 47, 73, 0.94)", color: "#cffafe", fontSize: "12px", fontWeight: 900, letterSpacing: "0.06em", textTransform: "uppercase" };
const arrowStyle = { position: "absolute", left: "50%", bottom: "-54px", color: "#67e8f9", fontSize: "46px", lineHeight: 1, filter: "drop-shadow(0 0 12px rgba(34, 211, 238, 0.95))", animation: "guidedTourArrowBounce 0.9s ease-in-out infinite alternate" };
const controlPanelStyle = { position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "16px", width: "min(420px, 100%)", padding: "28px", border: "1px solid rgba(56, 189, 248, 0.28)", borderRadius: "18px", background: "rgba(15, 23, 42, 0.45)", boxShadow: "0 24px 80px rgba(0, 0, 0, 0.42)", color: "#f8fafc", textAlign: "center" };
const stepLabelStyle = { margin: 0, color: "#67e8f9", fontSize: "12px", fontWeight: 900, letterSpacing: "0.08em", textTransform: "uppercase" };
const buttonRowStyle = { display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "12px" };
const primaryButtonStyle = { minHeight: "44px", padding: "10px 18px", border: "1px solid rgba(34, 211, 238, 0.42)", borderRadius: "10px", background: "linear-gradient(135deg, #22d3ee, #14b8a6)", color: "#05202a", fontSize: "14px", fontWeight: 900, cursor: "pointer" };
const secondaryButtonStyle = { minHeight: "44px", padding: "10px 16px", border: "1px solid rgba(248, 250, 252, 0.28)", borderRadius: "10px", background: "rgba(15, 23, 42, 0.96)", color: "#f8fafc", fontSize: "14px", fontWeight: 800, cursor: "pointer" };

function getTarget(title) {
  const selector = TOUR_SELECTOR_BY_TITLE[title];
  if (selector) {
    const selectorTarget = document.querySelector(selector);
    if (selectorTarget) {
      const rect = selectorTarget.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) return { element: selectorTarget, rect };
    }
  }

  const tourId = TOUR_ID_BY_TITLE[title];
  if (tourId) {
    const stableTarget = document.querySelector(`[data-tour-id="${tourId}"]`);
    if (stableTarget) {
      const rect = stableTarget.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) return { element: stableTarget, rect };
    }
  }

  const normalizedTitle = title.trim().toLowerCase();
  const candidates = Array.from(document.querySelectorAll("h1, h2, h3, h4, h5, h6, button, a, [role='button'], [aria-label]"))
    .filter((element) => !element.closest("[data-guided-tour-overlay]"))
    .map((element) => ({ element, text: `${element.textContent || ""} ${element.getAttribute("aria-label") || ""}`.trim().toLowerCase() }))
    .filter(({ text }) => text.includes(normalizedTitle))
    .map(({ element }) => ({ element, rect: element.getBoundingClientRect() }))
    .filter(({ rect }) => rect.width > 0 && rect.height > 0)
    .sort((a, b) => (a.rect.width * a.rect.height) - (b.rect.width * b.rect.height));

  return candidates[0] || null;
}

function rectWithPadding(rect) {
  const padding = 12;
  return {
    top: Math.max(12, rect.top - padding),
    left: Math.max(12, rect.left - padding),
    width: Math.min(window.innerWidth - 24, rect.width + padding * 2),
    height: Math.min(window.innerHeight - 24, rect.height + padding * 2),
  };
}

function GuidedTourOverlay({ onExit, onStepChange = () => {} }) {
  const [started, setStarted] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [targetRect, setTargetRect] = useState(FALLBACK_RECT);

  const activeStep = TOUR_STEPS[stepIndex];
  const isFinalStep = stepIndex === TOUR_STEPS.length - 1;

  useEffect(() => {
    if (started) {
      onStepChange({ ...activeStep, index: stepIndex, total: TOUR_STEPS.length });
    }
  }, [activeStep, onStepChange, started, stepIndex]);

  useEffect(() => {
    if (!started) return undefined;

    const target = getTarget(activeStep.title);
    if (!target) {
      setTargetRect(FALLBACK_RECT);
      return undefined;
    }

    const isOutsideViewport = target.rect.top < 24 || target.rect.bottom > window.innerHeight - 24;
    if (isOutsideViewport) {
      target.element.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
    }

    const updateTarget = () => {
      const currentTarget = getTarget(activeStep.title);
      setTargetRect(currentTarget ? rectWithPadding(currentTarget.rect) : FALLBACK_RECT);
    };

    const settleTimer = window.setTimeout(updateTarget, isOutsideViewport ? 360 : 0);
    window.addEventListener("resize", updateTarget);
    window.addEventListener("scroll", updateTarget, true);

    return () => {
      window.clearTimeout(settleTimer);
      window.removeEventListener("resize", updateTarget);
      window.removeEventListener("scroll", updateTarget, true);
    };
  }, [activeStep.title, started]);

  const spotlightStyle = {
    position: "fixed",
    top: targetRect.top,
    left: targetRect.left,
    width: targetRect.width,
    height: targetRect.height,
    border: "3px solid #22d3ee",
    borderRadius: "20px",
    boxShadow: "0 0 0 9999px rgba(2, 6, 23, 0.10), 0 0 36px rgba(34, 211, 238, 0.72)",
    pointerEvents: "none",
    transition: "top 220ms ease, left 220ms ease, width 220ms ease, height 220ms ease",
  };

  const handleStart = () => { setStarted(true); setStepIndex(0); setPaused(false); };
  const handleRestart = () => { setStepIndex(0); setPaused(false); };
  const handleRepeat = () => { setStepIndex(0); setPaused(false); };
  const handlePrevious = () => { if (!paused) setStepIndex((current) => Math.max(current - 1, 0)); };
  const handleNext = () => { if (!paused) setStepIndex((current) => Math.min(current + 1, TOUR_STEPS.length - 1)); };

  return (
    <div data-guided-tour-overlay style={overlayStyle} role="dialog" aria-modal="true" aria-labelledby="guided-tour-title">
      <style>{`@keyframes guidedTourArrowBounce { from { transform: translate(-50%, 0); } to { transform: translate(-50%, 12px); } }`}</style>
      {started && (
        <div style={spotlightStyle} aria-hidden="true">
          <p style={spotlightLabelStyle}>{activeStep.title}</p>
        
        </div>
      )}
      <section style={controlPanelStyle}>
        <span aria-hidden="true">🚀</span>
        {started ? (
          <>
            <p style={stepLabelStyle}>Step {stepIndex + 1} of {TOUR_STEPS.length}{paused ? " · Paused" : ""}</p>
            <h2 id="guided-tour-title">{activeStep.title}</h2>
            <p>{activeStep.description}</p>
            <div style={buttonRowStyle}>
              {stepIndex > 0 && <button type="button" style={secondaryButtonStyle} onClick={handlePrevious} disabled={paused}>Previous</button>}
              {!isFinalStep && <button type="button" style={primaryButtonStyle} onClick={handleNext} disabled={paused}>Next</button>}
              <button type="button" style={secondaryButtonStyle} onClick={() => setPaused((current) => !current)}>{paused ? "Resume" : "Pause"}</button>
              <button type="button" style={secondaryButtonStyle} onClick={handleRestart}>Restart</button>
              {isFinalStep && <button type="button" style={primaryButtonStyle} onClick={handleRepeat}>Repeat</button>}
              <button type="button" style={secondaryButtonStyle} onClick={onExit}>Exit Tour</button>
            </div>
          </>
        ) : (
          <>
            <h2 id="guided-tour-title">Guided Tour</h2>
            <p>Explore AninaTrade one feature at a time. The tour is informational only and cannot place trades or modify your data.</p>
            <div style={buttonRowStyle}>
              <button type="button" style={primaryButtonStyle} onClick={handleStart}>Start Tour</button>
              <button type="button" style={secondaryButtonStyle} onClick={onExit}>Exit Tour</button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

export default GuidedTourOverlay;